import {
  Card,
  Group,
  Text,
  Badge,
  ActionIcon,
  Stack,
  SimpleGrid,
  Title,
  Divider,
  Tooltip,
  Paper,
  Center,
  Button,
} from '@mantine/core';
import {
  IconPencil,
  IconTrash,
  IconPlayerPause,
  IconPlayerPlay,
  IconCalendarRepeat,
  IconInfoCircle,
} from '@tabler/icons-react';
import type { DosingSchedule } from '@/types/dosing';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface DosingSchedulesListProps {
  schedules: DosingSchedule[];
  onEdit: (schedule: DosingSchedule) => void;
  onDelete: (schedule: DosingSchedule) => void;
  onToggleActive: (schedule: DosingSchedule) => void;
  onCreateNew?: () => void;
}

function ScheduleCard({
  schedule,
  onEdit,
  onDelete,
  onToggleActive,
}: {
  schedule: DosingSchedule;
  onEdit: (schedule: DosingSchedule) => void;
  onDelete: (schedule: DosingSchedule) => void;
  onToggleActive: (schedule: DosingSchedule) => void;
}) {
  const isEnded = !schedule.is_active;

  let badgeColor: string;
  let badgeText: string;

  if (!schedule.is_active) {
    badgeColor = 'gray';
    badgeText = 'Ended';
  } else if (schedule.end_date) {
    badgeColor = 'green';
    badgeText = `Active \u00B7 Ends ${formatDate(schedule.end_date)}`;
  } else {
    badgeColor = 'green';
    badgeText = 'Active \u00B7 Ongoing';
  }

  return (
    <Card withBorder style={{ opacity: isEnded ? 0.7 : 1 }}>
      <Group justify="space-between" mb="xs">
        <Text fw={500}>{schedule.chemical_name}</Text>
        <Badge
          color={badgeColor}
          variant="dot"
        >
          {badgeText}
        </Badge>
      </Group>

      <Text size="sm" c="dimmed">
        {Number(schedule.volume_ml).toFixed(1)} mL · {schedule.frequency}
      </Text>

      {schedule.brand && (
        <Text size="sm" c="dimmed">
          {schedule.brand}
        </Text>
      )}

      <Text size="xs" c="dimmed" mt="xs">
        Started: {formatDate(schedule.start_date)}
      </Text>

      {schedule.end_date && (
        <Text size="xs" c="dimmed">
          End: {formatDate(schedule.end_date)}
        </Text>
      )}

      <Text size="xs" c="dimmed" mt="xs">
        {schedule.event_count} events
      </Text>

      <Divider my="sm" />

      <Group justify="flex-end" gap="xs">
        <Tooltip label="Edit">
          <ActionIcon variant="subtle" onClick={() => onEdit(schedule)}>
            <IconPencil size={16} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={schedule.is_active ? 'Pause' : 'Resume'}>
          <ActionIcon variant="subtle" onClick={() => onToggleActive(schedule)}>
            {schedule.is_active ? (
              <IconPlayerPause size={16} />
            ) : (
              <IconPlayerPlay size={16} />
            )}
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Delete">
          <ActionIcon variant="subtle" color="red" onClick={() => onDelete(schedule)}>
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );
}

export function DosingSchedulesList({
  schedules,
  onEdit,
  onDelete,
  onToggleActive,
  onCreateNew,
}: DosingSchedulesListProps) {
  if (schedules.length === 0) {
    return (
      <Paper p="xl" withBorder radius="md">
        <Center>
          <Stack align="center" gap="md">
            <IconCalendarRepeat size={48} style={{ opacity: 0.5 }} />
            <Text size="xl" fw={600}>
              No Dosing Schedules Yet
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={400}>
              Create a dosing schedule to track your regular supplement routine
              and automatically log dosing events.
            </Text>
            {onCreateNew && (
              <Button onClick={onCreateNew}>Create Your First Schedule</Button>
            )}
          </Stack>
        </Center>
      </Paper>
    );
  }

  const activeSchedules = schedules.filter((s) => s.is_active);
  const pastSchedules = schedules.filter((s) => !s.is_active);

  return (
    <Stack gap="lg">
      {activeSchedules.length > 0 && (
        <div>
          <Group gap="xs" mb="md">
            <Title order={4}>Active Schedules</Title>
            <Tooltip label="Active schedules automatically log dosing events at the specified frequency.">
              <IconInfoCircle size={16} style={{ opacity: 0.5 }} />
            </Tooltip>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {activeSchedules.map((schedule) => (
              <ScheduleCard
                key={schedule.id}
                schedule={schedule}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleActive={onToggleActive}
              />
            ))}
          </SimpleGrid>
        </div>
      )}

      {pastSchedules.length > 0 && (
        <div>
          <Title order={4} mb="md">
            Past Schedules
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {pastSchedules.map((schedule) => (
              <ScheduleCard
                key={schedule.id}
                schedule={schedule}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleActive={onToggleActive}
              />
            ))}
          </SimpleGrid>
        </div>
      )}
    </Stack>
  );
}
